import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  Search, 
  ArrowRight
} from 'lucide-react';

const SearchResultsPage = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const type = searchParams.get('type') || '';
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, services: 0, projects: 0 });
  const [error, setError] = useState(null);
  const [contentReady, setContentReady] = useState(false);

  // Fade-in animasyonu için
  useEffect(() => {
    const timer = setTimeout(() => {
      setContentReady(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const fetchResults = async () => {
      if (!query.trim()) {
        setResults([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      
      try {
        const apiBase = process.env.NODE_ENV === 'development' 
          ? 'http://localhost:8000' 
          : `${window.location.protocol}//${window.location.host}`;
        
        let apiUrl;
        
        // If type=blog, search only in blogs
        if (type === 'blog') {
          apiUrl = `${apiBase}/backend/api.php?action=blogSearch&q=${encodeURIComponent(query)}`;
        } else {
          // Default global search
          apiUrl = `${apiBase}/backend/api.php?action=globalSearch&q=${encodeURIComponent(query)}`;
        }
          
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Arama başarısız oldu');
        }

        let data;
        const responseText = await response.text();
        try {
          data = JSON.parse(responseText);
        } catch (jsonError) {
          console.error('JSON parse error:', jsonError);
          console.error('Response text:', responseText);
          throw new Error('Geçersiz JSON yanıtı alındı');
        }
        
        // Transform backend data to match UI format
        const transformedResults = (data.results || []).map(item => {
          // URL'ye göre web sitesi tasarımı kontrolü
          const isWebsiteDesign = (url) => {
            if (!url) return false;
            const websiteDesignPaths = [
              '/hazir-web-sitesi-tasarimlari/',
              '/eticaret-tasarimlari/',
              '/kurumsal-web-sitesi-tasarimlari/',
              '/web-sitesi-tasarimlari/'
            ];
            return websiteDesignPaths.some(path => url.includes(path));
          };
          
          // Result type'a göre Türkçe label ve renk belirleme
          const getResultLabel = (resultType, url) => {
            // Eğer result_type 'design' veya 'template' ise ve URL web sitesi tasarımı path'lerinden birini içeriyorsa
            if ((resultType === 'design' || resultType === 'template') && isWebsiteDesign(url)) {
              return { label: 'Web Sitesi Tasarımı', color: 'bg-teal-600' };
            }
            
            const labels = {
              'service': { label: 'Hizmet', color: 'bg-blue-600' },
              'project': { label: 'Proje', color: 'bg-pink-600' },
              'blog': { label: 'Blog', color: 'bg-purple-600' },
              'faq': { label: 'SSS', color: 'bg-yellow-600' },
              'education': { label: 'Eğitim', color: 'bg-indigo-600' },
              'design': { label: 'Tasarım', color: 'bg-rose-600' },
              'template': { label: 'Web Sitesi Tasarımı', color: 'bg-teal-600' },
              'hosting': { label: 'Hosting', color: 'bg-green-600' },
              'vps': { label: 'VPS Sunucu', color: 'bg-orange-600' },
              'vds': { label: 'VDS Sunucu', color: 'bg-amber-600' },
              'ssl': { label: 'SSL Sertifika', color: 'bg-lime-600' },
              'server': { label: 'Sunucu', color: 'bg-red-600' },
              'package': { label: 'Paket', color: 'bg-violet-600' },
              'page': { label: 'Sayfa', color: 'bg-slate-600' }
            };
            
            return labels[resultType] || { label: 'Genel', color: 'bg-gray-600' };
          };
          
          const resultUrl = item.url || '';
          const resultInfo = getResultLabel(item.result_type, resultUrl);
          const categoryLabel = resultInfo.label;
          const badgeColor = resultInfo.color;
          
          // Button text'i result type'a göre belirle
          const getButtonText = (resultType) => {
            const buttonTexts = {
              'blog': 'Oku',
              'faq': 'Görüntüle',
              'education': 'İncele',
              'service': 'Detay',
              'project': 'İncele',
              'design': 'İncele',
              'template': 'İncele',
              'hosting': 'Paketi Gör', // Hosting paketleri için
              'vps': 'Paketi Gör',
              'vds': 'Paketi Gör',
              'ssl': 'Paketi Gör',
              'server': 'Paketi Gör',
              'package': 'Paketi Gör',
              'page': 'Görüntüle' // Sayfalar için
            };
            
            return buttonTexts[resultType] || 'İncele';
          };
          
          return {
            id: item.id,
            type: item.result_type || 'blog',
            title: item.title || item.name,
            category: categoryLabel,
            categoryColor: badgeColor,
            buttonText: getButtonText(item.result_type),
            author: item.author || 'Website Al',
            publishDate: item.publishDate || item.published_at,
            readTime: item.readTime || '5 dakika',
            views: item.views || 0,
            likes: item.likes || 0,
            excerpt: item.excerpt || item.description || '',
            tags: item.tags || [],
            url: item.url || `/blog/${item.id || item.slug}`
          };
        });

        setResults(transformedResults);
        setStats(data.stats || { total: 0, services: 0, projects: 0 });
        
      } catch (err) {
        console.error('Search error:', err);
        setError(err.message);
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [query, type]);

  const handleSearch = (e) => {
    e.preventDefault();
    const searchInput = e.target.querySelector('input[type="search"]') || e.target.querySelector('input[type="text"]');
    const searchQuery = searchInput?.value?.trim() || query;
    if (searchQuery) {
      window.location.href = `/arama?q=${encodeURIComponent(searchQuery)}`;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with Search Input */}
      <div 
        className="bg-gradient-to-r from-purple-600 to-blue-600 text-white py-12"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="container mx-auto px-6">
          <h1 className="text-3xl font-bold mb-6">
            Arama Sonuçları
          </h1>
          
          {/* Search Input */}
          <form onSubmit={handleSearch} className="max-w-2xl">
            <div className="relative">
              <input
                type="search"
                defaultValue={query}
                placeholder="Ara..."
                className="w-full px-6 py-4 pl-14 pr-14 text-gray-900 rounded-lg shadow-lg focus:ring-4 focus:ring-white focus:ring-opacity-50 focus:outline-none text-lg"
                autoFocus
              />
              <Search className="absolute left-5 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <button
                type="submit"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
              >
                Ara
              </button>
            </div>
          </form>
          
          {!loading && query && (
            <p className="mt-4 text-purple-100">
              "{query}" için {stats.total} sonuç bulundu
            </p>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div 
        className="max-w-7xl mx-auto px-6 py-8"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <main>
          {/* Results Summary */}
          {!loading && query && results.length > 0 && (
            <div className="mb-6">
              <p className="text-gray-600">
                <span className="font-semibold text-gray-900">{stats.total}</span> sonuç bulundu
                {query && (
                  <span> "{query}" için</span>
                )}
              </p>
            </div>
          )}

          {loading ? (
            <div className="text-center py-20">
              <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full mx-auto"></div>
              <p className="mt-4 text-gray-600">Sonuçlar yükleniyor...</p>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Search className="w-10 h-10 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Bir Hata Oluştu</h2>
              <p className="text-gray-600 mb-6">{error}</p>
              <Button onClick={() => window.location.reload()} className="bg-purple-600 hover:bg-purple-700">
                Tekrar Dene
              </Button>
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-20">
              <Search className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Sonuç Bulunamadı</h2>
              <p className="text-gray-600 mb-6">"{query}" için hiçbir sonuç bulunamadı. Farklı anahtar kelimeler deneyebilirsiniz.</p>
              <Button asChild>
                <Link to="/">Anasayfaya Dön</Link>
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {results.map((result) => (
                <Link key={result.id} to={result.url} className="block">
                  <Card className="cursor-pointer">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          {/* Category Badge */}
                          <Badge className={`mb-3 ${result.categoryColor || 'bg-purple-600'} text-white`}>
                            {result.category}
                          </Badge>

                          {/* Title */}
                          <h3 className="text-xl font-bold text-gray-900 mb-2">
                            {result.title}
                          </h3>
                          
                          {/* Description/Excerpt */}
                          {result.excerpt && (
                            <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                              {result.excerpt}
                            </p>
                          )}
                          
                          {/* Blog specific info */}
                          {result.type === 'blog' && result.publishDate && (
                            <div className="mt-2 text-sm text-gray-600">
                              <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                                <span>👤 {result.author}</span>
                                <span>📅 {new Date(result.publishDate).toLocaleDateString('tr-TR')}</span>
                                {result.readTime && <span>⏱️ {result.readTime}</span>}
                                {result.views > 0 && <span>👁️ {result.views}</span>}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* CTA Button */}
                        <Button size="sm" className="ml-4">
                          {result.buttonText || 'İncele'}
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default SearchResultsPage;

